import { DataStoreInvalidBucketNameError, DataStoreInvalidRegionError, DataStoreKind, DataStoreLimitExceededError, DataStoresError, } from '#external-files/types';
export const MAX_DATA_STORE_NAME_LENGTH = 64;
export const MAX_DATA_STORES_PER_FILESPACE = 1000;
export const INVALID_DATA_STORE_NAME_ERROR = 'Data store name can only contain letters, numbers, spaces, underscores, and hyphens';
const VALID_DATA_STORE_NAME_CHARS = /^[\p{L}\p{M}\p{N} _-]+$/u;
const BUCKET_NAME_PATTERN_VIRTUAL = /^[\da-z]([\d.a-z-])*[\da-z]$/;
const BUCKET_NAME_PATTERN_PATH = /^[\da-z]([\d._a-z-])*[\da-z]$/;
const IP_ADDRESS_PATTERN = /^(?:\d{1,3}\.){3}\d{1,3}$/;
const VALID_REGION_PATTERN = /^[\da-z-]+$/;
export const MIN_BUCKET_NAME_LENGTH = 3;
export const MAX_BUCKET_NAME_LENGTH = 63;
export function normalizeDataStoreName(name) {
    const trimmed = name?.trim() ?? '';
    return trimmed.normalize('NFD');
}
export function validateDataStoreName(name) {
    if (name.trim().length === 0) {
        throw new DataStoresError('Data store name cannot be empty or whitespace-only');
    }
    if (name.length > MAX_DATA_STORE_NAME_LENGTH) {
        throw new DataStoresError(`Data store name must be at most ${MAX_DATA_STORE_NAME_LENGTH} characters (got ${name.length})`);
    }
    if (!VALID_DATA_STORE_NAME_CHARS.test(name)) {
        throw new DataStoresError(INVALID_DATA_STORE_NAME_ERROR);
    }
}
function isValidIpAddress(value) {
    if (!IP_ADDRESS_PATTERN.test(value)) {
        return false;
    }
    const octets = value.split('.');
    return octets.every((octet) => {
        const num = Number.parseInt(octet, 10);
        return num >= 0 && num <= 255;
    });
}
function validateBucketNameLength(bucketName) {
    if (bucketName.length < MIN_BUCKET_NAME_LENGTH || bucketName.length > MAX_BUCKET_NAME_LENGTH) {
        throw new DataStoreInvalidBucketNameError(`Bucket name must be between ${MIN_BUCKET_NAME_LENGTH} and ${MAX_BUCKET_NAME_LENGTH} characters (got ${bucketName.length})`);
    }
}
function validateVirtualAddressingRules(bucketName) {
    if (!BUCKET_NAME_PATTERN_VIRTUAL.test(bucketName)) {
        throw new DataStoreInvalidBucketNameError('Bucket name must contain only lowercase letters, numbers, dots, and hyphens, and must start and end with a letter or number');
    }
    if (bucketName.includes('..')) {
        throw new DataStoreInvalidBucketNameError('Bucket name cannot contain consecutive dots');
    }
    if (isValidIpAddress(bucketName)) {
        throw new DataStoreInvalidBucketNameError('Bucket name cannot be formatted as an IP address');
    }
}
function validatePathStyleRules(bucketName) {
    if (!BUCKET_NAME_PATTERN_PATH.test(bucketName)) {
        throw new DataStoreInvalidBucketNameError('Bucket name must contain only lowercase letters, numbers, dots, hyphens, and underscores, and must start and end with a letter or number');
    }
}
export function validateBucketName(bucketName, useVirtualAddressing) {
    if (!bucketName) {
        throw new DataStoreInvalidBucketNameError('Bucket name cannot be empty');
    }
    validateBucketNameLength(bucketName);
    if (useVirtualAddressing) {
        validateVirtualAddressingRules(bucketName);
    }
    else {
        validatePathStyleRules(bucketName);
    }
}
export function validateRegion(region, endpointOverride) {
    const hasEndpoint = endpointOverride && endpointOverride.trim().length > 0;
    const hasRegion = region && region.trim().length > 0;
    if (!hasRegion && !hasEndpoint) {
        throw new DataStoresError('Either region or endpointOverride must be provided');
    }
    if (hasRegion && !VALID_REGION_PATTERN.test(region)) {
        throw new DataStoreInvalidRegionError('Region can only contain lowercase letters, numbers, and hyphens');
    }
}
export function validateAccessKey(accessKey) {
    if (!accessKey) {
        throw new DataStoresError('Access key cannot be empty');
    }
}
export function validateSecretKey(secretKey) {
    if (!secretKey) {
        throw new DataStoresError('Secret key cannot be empty');
    }
}
export function validateEndpointOverride(endpoint) {
    if (!endpoint || endpoint.trim().length === 0) {
        return;
    }
    if (!endpoint.includes('://')) {
        try {
            new URL(`http://${endpoint}`);
        }
        catch {
            throw new DataStoresError(`Invalid endpoint URL: ${endpoint}`);
        }
        return;
    }
    let url;
    try {
        url = new URL(endpoint);
    }
    catch {
        throw new DataStoresError(`Invalid endpoint URL: ${endpoint}`);
    }
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        throw new DataStoresError(`Endpoint must use http:// or https:// protocol (got ${url.protocol})`);
    }
}
export function validateDataStoreCount(currentCount) {
    if (currentCount >= MAX_DATA_STORES_PER_FILESPACE) {
        throw new DataStoreLimitExceededError('Maximum number of data stores reached.');
    }
}
function isS3DataStore(dataStore) {
    return dataStore.kind === DataStoreKind.S3;
}
function normalizeS3DataStore(dataStore) {
    return {
        ...dataStore,
        accessKey: dataStore.accessKey.trim(),
        secretKey: dataStore.secretKey.trim(),
        endpointOverride: dataStore.endpointOverride?.trim() ?? '',
    };
}
export function normalizeDataStore(dataStore) {
    if (isS3DataStore(dataStore)) {
        return normalizeS3DataStore(dataStore);
    }
    return dataStore;
}
function validateS3DataStoreParams(dataStore) {
    validateBucketName(dataStore.bucketName, dataStore.useVirtualAddressing);
    validateRegion(dataStore.region, dataStore.endpointOverride);
    validateAccessKey(dataStore.accessKey);
    validateSecretKey(dataStore.secretKey);
    validateEndpointOverride(dataStore.endpointOverride);
}
export function validateDataStore(dataStore) {
    if (isS3DataStore(dataStore)) {
        validateS3DataStoreParams(dataStore);
    }
}
